import { cloneElement, Fragment, ReactNode, useContext } from "react"
import { useStore } from "react-redux"
import { bodyContent } from "@/store/apis/matrixApi"
import { createSelector } from "@reduxjs/toolkit"
import { get } from "lodash"
import {
  isLocal,
  isUat,
  replacePropsReturnXml,
  replacePropsInString,
} from "@/utils/helpers/formatters"
import { RootState } from "@/store"
import htmlParse, { HTMLReactParserOptions } from "html-react-parser"
import { ContentMapAnchor } from "@components/Utils/ContentMap"
import { ContentMapContext } from "@/utils/contentMapContext"
import {
  ContentMapPathRegister,
  useContentMapRegistration,
} from "@/utils/contentMapRegister"
import {
  returnObjectWithPath,
  type ContentProps,
  type UseContentProps,
} from "@/utils/useContentProps"

export type { ContentProps, UseContentProps }
export { returnObjectWithPath }

const selectBodyContent = createSelector(
  bodyContent,
  (_state: RootState, obj: UseContentProps) => returnObjectWithPath(obj),
  (content, obj) => {
    return { content: get(content, obj.path), ...obj }
  }
)

const missingMatrixContent: MatrixContent = {
  contentBody: "",
  contentHeading: "",
  contentFooter: "",
  valOne: "",
  valTwo: "",
  valThree: "",
  valFour: "",
  valFive: "",
  url: "",
  status: "",
}

const selectBodyContentAsJsx = createSelector(
  selectBodyContent,
  (obj): MatrixContent => {
    const { path, content, noParse, pipe, stripHtml } = obj
    if (content) {
      const id = content?.assetId
      const pipeChar = typeof pipe === "string" ? pipe : undefined

      return Object.keys(content)
        .filter((s) => !["assetId"].includes(s))
        .reduce((a: any, b: string) => {
          if ("string" === typeof content[b]) {
            if (pipeChar) {
              const parts = content[b].split(pipeChar)
              a[b] = noParse
                ? parts
                : parts.map((part) =>
                    replacePropsReturnXml(part, obj, { prop: b, id })
                  )
            } else {
              const shouldStrip =
                stripHtml === true ||
                (Array.isArray(stripHtml) &&
                  stripHtml.includes(b as keyof MatrixContent))
              a[b] = noParse
                ? content[b]
                : shouldStrip
                  ? replacePropsInString(content[b], obj).replace(
                      /<[^>]*>/g,
                      ""
                    )
                  : replacePropsReturnXml(content[b], obj, { prop: b, id })
            }
          } else {
            a[b] = content[b]
          }
          return a
        }, {})
    }

    // local/UAT only — keep missing-content noise out of production consoles
    if (path && (isLocal() || isUat())) {
      console.warn(`Missing matrix content`, obj)
    }

    // components are implemented on the basis the matrix
    // content exists so instead of crashing the app we
    // can try and keep it running with the missing content
    return missingMatrixContent
  }
)

/**
 * Pulls content from matrix data by passing in a string path
 * and returns the JSX version of the content
 * ie dashboard.general.gsu_member -> details: ["Member name ", { key:"1", type:"p", props:{...}}]
 */
export type MatrixContentType = ReturnType<typeof replacePropsReturnXml>

export type MatrixContent<
  T extends Record<string, string> = { [key: string]: string },
> = {
  contentIntro?: MatrixContentType
  contentBody?: MatrixContentType
  contentFooter?: MatrixContentType
  contentHeading?: string
  valOne?: string
  valTwo?: string
  valThree?: string
  valFour?: string
  valFive?: string
  url?: string
  status?: string
  csvData?: T[]
  /** Content Map debug only — rendered bubble anchor for manual placement (e.g. on CTA buttons). Undefined when the content map is disabled. */
  contentMapAnchor?: React.ReactNode
}

export type MatrixContentNoParse = Record<
  Exclude<keyof MatrixContent, "contentMapAnchor">,
  string
> & { contentMapAnchor?: React.ReactNode }

export type MatrixContentPiped = Omit<
  MatrixContent,
  "contentIntro" | "contentBody" | "contentFooter"
> & {
  contentIntro?: MatrixContentType[]
  contentBody?: MatrixContentType[]
  contentFooter?: MatrixContentType[]
}

export type MatrixCSVData<T extends Record<string, string>> = Pick<
  Required<MatrixContent<T>>,
  "csvData"
>

export function useContentJsx<T extends UseContentProps>(props: T) {
  const store = useStore<RootState>()
  const result = selectBodyContentAsJsx(store.getState(), props) as T extends {
    noParse: true
  }
    ? MatrixContentNoParse
    : T extends { pipe: string }
      ? MatrixContentPiped
      : MatrixContent

  const contentMap = useContext(ContentMapContext)
  const normalizedProps = returnObjectWithPath(props)

  // Re-register when Matrix bodyContent finishes loading so entries are not stuck as failed
  useContentMapRegistration(props)

  if (contentMap?.isEnabled) {
    const { content } = selectBodyContent(store.getState(), normalizedProps)
    if (content) {
      const refId = contentMap.getOrAssignRefId(normalizedProps.path)
      const anchor = (
        <ContentMapAnchor refId={refId} path={normalizedProps.path} />
      )
      const injected = { ...result } as any
      // Inject into every non-empty renderable field so the sentinel is present
      // regardless of which field the consumer renders.
      let done = false
      for (const field of [
        "contentBody",
        "contentIntro",
        "contentHeading",
      ] as const) {
        if (injected[field] != null && injected[field] !== "") {
          injected[field] = (
            <>
              {anchor}
              {injected[field]}
            </>
          )
          done = true
        }
      }
      if (!done) {
        injected.contentBody = (
          <>
            {anchor}
            {injected.contentBody ?? null}
          </>
        )
      }
      // Also expose the anchor for manual placement (e.g. on CTA buttons)
      injected.contentMapAnchor = anchor
      return injected as typeof result
    }
  }

  return result
}

/**
 * Pulls content from matrix data by passing in a string path
 * and returns the string version of the content
 * ie dashboard.general.gsu_member -> details: "Member name <p>string version</p>"
 */
export function useContent(props: UseContentProps) {
  useContentMapRegistration(props)
  return useUnsubscribeSelector((state: RootState) =>
    selectBodyContent(state, props)
  )
}

function useUnsubscribeSelector<Value>(fn: (state: RootState) => Value) {
  const store = useStore()
  return fn(store.getState())
}

export function useContentWithCustomReplace(
  props: UseContentProps,
  replace: any
) {
  const { content } = useContent(props)
  return htmlParse(content.contentBody, { replace })
}

/**
 * Takes an array of paths and a props object (keywords) and returns
 * jsx for each of the paths in the array with the keys set
 */
export function useContentJsxArray() {
  return (paths: string[], keywords: any) => {
    const store = useStore<RootState>()
    const contentMap = useContext(ContentMapContext)

    const items = paths.map(
      (path) =>
        selectBodyContentAsJsx(store.getState(), {
          path,
          ...(keywords ?? {}),
        })!
    )

    return items.map((item: any, index: number) => {
      const path = paths[index]
      const normalized = { path, ...(keywords ?? {}) }

      if (contentMap?.isEnabled) {
        const { content } = selectBodyContent(store.getState(), normalized)
        if (content) {
          const refId = contentMap.getOrAssignRefId(path)
          return (
            <Fragment key={index}>
              <ContentMapPathRegister props={normalized} />
              <ContentMapAnchor refId={refId} path={path} />
              {cloneElement(item.contentBody)}
            </Fragment>
          )
        }
        return (
          <Fragment key={index}>
            <ContentMapPathRegister props={normalized} />
            {cloneElement(item.contentBody, { key: index })}
          </Fragment>
        )
      }

      return cloneElement(item.contentBody, { key: index })
    })
  }
}

/**
 * Returns a useContentJsx method where subsequent calls auto prepend
 * the prefix to the path making it shorter to reference deeply nested items
 */
export function usePrefixedContentJsx<
  E extends UseContentProps = UseContentProps,
>(prefix: string) {
  return <T extends E>(props: T) => {
    const { path, ...rest } = returnObjectWithPath(props)
    // empty path skips Matrix lookup (no missing-content warn) — used by optional receipt keys
    return useContentJsx({
      ...rest,
      path: path ? `${prefix}.${path}` : path,
    } as unknown as T)
  }
}

/**
 */
export function usePrefixedContentJsxArray(prefix: string) {
  return (paths: string[], keywords: any) => {
    const store = useStore<RootState>()
    const contentMap = useContext(ContentMapContext)

    const items = paths.map((path) =>
      selectBodyContentAsJsx(store.getState(), {
        path: path ? `${prefix}.${path}` : path,
        ...(keywords ?? {}),
      })
    )

    return items.map((item: any, index: number) => {
      const path = paths[index]
      const fullPath = path ? `${prefix}.${path}` : path
      const normalized = { path: fullPath, ...(keywords ?? {}) }

      if (contentMap?.isEnabled) {
        const { content } = selectBodyContent(store.getState(), normalized)
        if (content) {
          const refId = contentMap.getOrAssignRefId(fullPath)
          return (
            <Fragment key={index}>
              <ContentMapPathRegister props={normalized} />
              <ContentMapAnchor refId={refId} path={fullPath} />
              {cloneElement(item.contentBody)}
            </Fragment>
          )
        }
        return (
          <Fragment key={index}>
            <ContentMapPathRegister props={normalized} />
            {cloneElement(item.contentBody, { key: index })}
          </Fragment>
        )
      }

      return cloneElement(item.contentBody, { key: index })
    })
  }
}
